import { AppDistributionFirstnet } from "@erinfo/brand-ui/src/storybook/other/app-distribution/firstnet/_"
import { Attribution } from "@erinfo/brand-ui/src/storybook/typography/attribution/_"
import { BorderBox } from "@erinfo/brand-ui/src/StyledComponents"
import { StyledButton } from "@erinfo/brand-ui/src/StyledComponents/v2"
import { domain, envFirstNetAuthUrl, urlSchemeProvider } from "@erinfo/env"
import { version } from "@erinfo/provider/package.json"
import { Dispatch, RootState } from "@erinfo/provider/src/store"
import { openWindowPopup } from "@erinfo/react-utils/src/helpers/open-window/popup"
import { encodeState } from "@erinfo/react-utils/src/helpers/string-encode"
import { useRematchDispatch } from "@erinfo/react-utils/src/hooks"
import { Box } from "@mui/material"
import { styled } from "@mui/material/styles"
import { useEffect, useRef } from "react"
import { useSelector } from "react-redux"
import { Navigate, useNavigate } from "react-router-dom"

const Landing = () => {
  const navigate = useNavigate()
  const refTemporaryEventListeners = useRef<((event: any) => void)[]>([])
  const { checkToken, setSnackbarMessage, storeProviderById } =
    useRematchDispatch((dispatch: Dispatch) => ({
      setDialogMessage: dispatch.notifications.setDialogMessage,
      storeProviderById: dispatch.user.storeProviderById,
      checkToken: dispatch.user.checkToken,
      signOut: dispatch.user.signOutFirstNet,
      setSnackbarMessage: dispatch.notifications.setSnackbarMessage,
    }))
  const user = useSelector((state: RootState) => state.user)

  useEffect(() => {
    checkToken()

    const preventZoom = (e) => {
      if (e?.touches?.length === 2) {
        e.preventDefault()
      }
    }
    if (/iPad|iPhone|iPod|Android/.test(navigator.userAgent)) {
      window.document.addEventListener(`touchmove`, preventZoom, {
        passive: false,
      })
    }
    return () => {
      console.log(`removing event listener`)
      window.removeEventListener(`touchmove`, preventZoom)

      for (const fn of refTemporaryEventListeners.current) {
        window.removeEventListener(`message`, fn) // cleaning up temporary event listeners
      }
    }
  }, [])

  const onClickSignIn = () => {
    let url: string = envFirstNetAuthUrl // remove existing state, if any
    if (process.env.NODE_ENV === `development`) {
      const pathname = `/oauth`
      // when a dev is working on a local device, "redirectUri" is used to redirect to the locally running app or "localhost" (in browser)
      const redirectUri = process.env.NATIVE_APP
        ? `${urlSchemeProvider}${pathname}`
        : `https://${window.location.host}${pathname}`
      url = `${url.replace(
        /&?state=.*$/,
        `&state=${encodeState({ redirectUri })}`,
      )}${
        process.env.AWS_STAGE === `production` ? `` : `&erinfolabtest=03192026`
      }`
    }
    console.log(`opening URL: `, url)

    if (process.env.NATIVE_APP || process.env.NODE_ENV === `development`) {
      window.open(url, `_self`)
      return
    }

    openWindowPopup(url)

    // Listen for messages from the popup
    const messageListener = (event: MessageEvent) => {
      if (!event.origin.includes(domain)) return

      const { type, ...payload } = event.data || { type: `` }

      if (type.startsWith(`OAUTH_`)) {
        window.removeEventListener(`message`, messageListener)

        if (type === `OAUTH_SUCCESS`) {
          void storeProviderById(payload)
          navigate(`/app/identify`)
        } else {
          setSnackbarMessage(
            `Authentication failed. Please try again in a few minutes.`,
          )
        }
      }
    }

    refTemporaryEventListeners.current.push(messageListener) // store the listener for cleanup on page unmount
    window.addEventListener(`message`, messageListener)
  }

  return user?.token ? (
    <Navigate to="/app/identify" />
  ) : (
    <Box
      display="flex"
      sx={{ flex: 1, flexDirection: `column`, justifyContent: `space-between` }}
    >
      <BorderBox sx={{ justifyContent: `space-between`, marginTop: 5 }}>
        <Box
          sx={{
            display: `flex`,
            flexDirection: `column`,
            alignItems: `center`,
          }}
        >
          <StyledButton
            variant="contained"
            sx={{ mt: 1, width: 318, mb: 2 }}
            onClick={onClickSignIn}
          >
            Sign in with FirstNet
          </StyledButton>
        </Box>
      </BorderBox>
      <StyledAttribution>
        <AppDistributionFirstnet className="sign-in__market-logo" />
        <Attribution className="sign-in__attribution" version={`v${version}`} />
      </StyledAttribution>
    </Box>
  )
}

const StyledAttribution = styled(`div`)({
  display: `flex`,
  flexDirection: `column`,
  alignItems: `center`,

  "& .sign-in__attribution": {
    marginTop: 5,
  },
})

export default Landing
